import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Users,
  CalendarClock,
  Upload,
  Building2,
  ChevronDown,
  FileDown,
  Paperclip,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { getService, services } from "@/lib/services-data";
import { Disclaimer } from "@/components/site/Disclaimer";
import { submitCosecQuote } from "@/lib/api/cosec.functions";

const SERVICE_SLUG = "company-secretarial";
const PAGE_URL = "https://nkadimengdannylcs.com/services/company-secretarial";

const FAQS: { q: string; a: string }[] = [
  {
    q: "What is Beneficial Ownership and do I need to file it with CIPC?",
    a: "Beneficial Ownership identifies the natural persons who ultimately own or control a company. Since 2023, the General Laws (Anti-Money Laundering and Combating Terrorism Financing) Amendment Act requires every company registered with CIPC to file and keep its Beneficial Ownership information up to date — usually within 10 business days of any change, and again together with the Annual Return. We prepare the disclosure, supporting affidavits and CIPC submission for you.",
  },
  {
    q: "Can you amend our Memorandum of Incorporation (MOI)?",
    a: "Yes. We draft, review and amend MOIs in line with the Companies Act 71 of 2008 — whether you need to convert a CoR15.1A short-form MOI into a long-form bespoke MOI, change share classes, add pre-emptive rights, restrict directors' authority, or align governance with a shareholders' agreement. The amended MOI is filed with CIPC using a CoR15.2 once the special resolution is signed.",
  },
  {
    q: "How do I update directors, the registered address or the auditor with CIPC?",
    a: "Director changes (appointments and resignations) are filed on CoR39, registered address changes on CoR21.1, and auditor / company secretary changes on CoR44. We collect the supporting documents (ID, consent letters, resolutions), prepare the resolutions and lodge the filings with CIPC on your behalf.",
  },
  {
    q: "Do private companies still have to hold an AGM?",
    a: "Only public and state-owned companies are required by the Companies Act to hold an AGM. Private companies are not legally required to do so unless their MOI says otherwise — but shareholders still need to pass annual resolutions (e.g. approving financial statements, appointing auditors where applicable, re-electing directors). We prepare the round-robin or written resolutions, signed registers and minute book entries.",
  },
  {
    q: "What is the difference between an annual and a special resolution?",
    a: "An ordinary (annual) resolution requires more than 50% of votes and covers routine matters such as approving financial statements or appointing auditors. A special resolution requires at least 75% and is needed for things like amending the MOI, issuing shares to directors, or approving a financial assistance under s44/s45. We draft both and the supporting board notice/minutes.",
  },
  {
    q: "What is a Foreigner Assurance application?",
    a: "A Foreigner Assurance is a CIPC verification process for non-South African directors or beneficial owners. We collect the certified passport, proof of address and supporting affidavits, complete the CIPC assurance form, and submit on your behalf so foreign directors can be properly recorded against the company.",
  },
  {
    q: "Are you registered as company secretaries?",
    a: "We provide company secretarial support as part of our legal & compliance practice. We are not admitted attorneys, and we don't replace the statutory company secretary that a public company must appoint under s86 of the Companies Act — but we handle the drafting, filings, records and governance work on behalf of private companies, NPOs and SMEs.",
  },
  {
    q: "How much do you charge for company secretarial work?",
    a: "Fees depend on the matter — most CIPC filings (director change, address change, Beneficial Ownership update) are quoted as a fixed fee, while MOI drafting, full governance retainers and meeting administration are quoted after a short scoping call. Use the Request a Quote form on this page and we'll come back within one business day.",
  },
];

const SUB_SERVICES = [
  "Beneficial Ownership filing",
  "Foreigner Assurance application",
  "MOI drafting / amendment",
  "Director / auditor / address change",
  "Annual or special resolutions",
  "AGM / board meeting administration",
  "Statutory records & minute book",
  "Governance retainer (ongoing)",
  "Other — please describe",
];

export const Route = createFileRoute("/services/company-secretarial")({
  head: () => {
    const title =
      "Company Secretarial Services South Africa | CIPC, MOI & Governance | NDLCS";
    const description =
      "Professional company secretarial services in South Africa — CIPC filings, Beneficial Ownership, MOI drafting, AGM administration and corporate governance support for startups, SMEs and NPOs.";
    const image =
      "https://nkadimengdannylcs.com/og-default.jpg";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        {
          name: "keywords",
          content:
            "company secretarial services South Africa, CIPC filings, Beneficial Ownership CIPC, MOI drafting, MOI amendment, AGM administration, company resolutions, corporate governance, statutory records, Companies Act 71 of 2008",
        },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "service" },
        { property: "og:url", content: PAGE_URL },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: PAGE_URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Company Secretarial Services",
            serviceType: "Company Secretarial & Corporate Governance Support",
            url: PAGE_URL,
            areaServed: { "@type": "Country", name: "South Africa" },
            provider: {
              "@type": "LegalService",
              name: "Nkadimeng Danny Legal and Compliance Support",
              url: "https://nkadimengdannylcs.com",
              areaServed: "ZA",
            },
            description:
              "CIPC filings, Beneficial Ownership submissions, Memorandum of Incorporation drafting and amendments, AGM and board meeting administration, statutory records and corporate governance support under the Companies Act 71 of 2008.",
            offers: {
              "@type": "Offer",
              priceCurrency: "ZAR",
              availability: "https://schema.org/InStock",
              url: PAGE_URL,
            },
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Company Secretarial Services",
              itemListElement: SUB_SERVICES.filter(
                (s) => s !== "Other — please describe",
              ).map((name) => ({
                "@type": "Offer",
                itemOffered: { "@type": "Service", name },
              })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map(({ q, a }) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://nkadimengdannylcs.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Services",
                item: "https://nkadimengdannylcs.com/services",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Company Secretarial Services",
                item: PAGE_URL,
              },
            ],
          }),
        },
      ],
    };
  },
  component: CompanySecretarialPage,
});

function CompanySecretarialPage() {
  const service = getService(SERVICE_SLUG)!;
  const related = services
    .filter((s) => s.slug !== SERVICE_SLUG)
    .slice(0, 3);

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <Link
            to="/services"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All services
          </Link>
          <div className="mt-6 flex items-start gap-5">
            <div className="hidden sm:inline-flex h-14 w-14 items-center justify-center rounded-md bg-navy text-gold">
              <Building2 className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <span className="gold-rule" />
              <h1 className="font-display text-4xl text-navy md:text-5xl">
                Company Secretarial Services in South Africa
              </h1>
              <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
                Corporate secretarial support for South African companies —
                CIPC filings, Beneficial Ownership submissions, MOI drafting
                and amendments, annual returns, resolutions and statutory
                records, handled end to end under the Companies Act 71 of 2008.
              </p>
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
                {service.intro}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/book"
                  search={{ service: SERVICE_SLUG }}
                  className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-semibold text-navy-foreground shadow-soft hover:bg-navy/90"
                >
                  <CalendarClock className="h-4 w-4" />
                  Book a consultation
                </Link>
                <a
                  href="#quote"
                  className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-semibold text-gold-foreground shadow-soft hover:opacity-95"
                >
                  Request a quote <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="/downloads/company-secretarial-documents-checklist.pdf"
                  download
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold text-navy hover:bg-secondary/60"
                >
                  <FileDown className="h-4 w-4" />
                  Download documents checklist
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-narrow grid gap-12 py-16 md:grid-cols-[1.4fr_1fr]">
        <div className="space-y-10">
          <div className="rounded-lg border border-border bg-secondary/40 p-5">
            <h2 className="font-display text-2xl text-navy">
              Why companies use our corporate secretarial service
            </h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
              {[
                "CIPC-compliant filings: CoR39, CoR21.1, CoR44, CoR15.2 and annual returns",
                "Beneficial Ownership registers filed and kept current within 10 business days",
                "Registered company: Reg No. 2026/518678/07, based in Limpopo, serving all of South Africa",
                "Fixed-fee quotes on standard CIPC filings, with a response within one business day",
                "Confidential and POPIA-aligned handling of directors' and shareholders' information",
                "Minute books, share registers and resolutions kept audit-ready",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-gold" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl text-navy">
              Overview of our company secretarial services
            </h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              {service.details}
            </p>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Whether you need once-off company secretarial support in South
              Africa or an ongoing corporate secretarial retainer, we keep your
              CIPC records, statutory registers and governance documents
              accurate so your company stays in good standing.
            </p>
          </div>


          <div>
            <h2 className="font-display text-2xl text-navy flex items-center gap-2">
              <Users className="h-5 w-5 text-gold" /> Who this is for
            </h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {service.whoFor.map((w) => (
                <li
                  key={w}
                  className="flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-gold" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl text-navy">What we offer</h2>
            <ul className="mt-6 space-y-3">
              {service.offerings.map((o) => (
                <li
                  key={o}
                  className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 shadow-soft"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-gold" />
                  <span className="text-foreground">{o}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl text-navy">How we work</h2>
            <ol className="mt-6 grid gap-4 sm:grid-cols-2">
              {service.process.map((p) => (
                <li
                  key={p.title}
                  className="rounded-lg border border-border bg-card p-5 shadow-soft"
                >
                  <p className="font-display text-base text-navy">{p.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {p.body}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <h2 className="font-display text-2xl text-navy">
              Scope of this service
            </h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              {service.scope}
            </p>
            <div className="mt-4">
              <Disclaimer variant="compact" />
            </div>
          </div>
        </div>

        <aside className="h-fit space-y-5 md:sticky md:top-28">
          <div className="rounded-xl bg-navy p-8 text-navy-foreground shadow-lifted">
            <h3 className="font-display text-xl">Ready to get started?</h3>
            <p className="mt-3 text-sm text-navy-foreground/80">
              Book a free 15-minute scoping call or send your company documents
              for a fixed-fee quote.
            </p>
            <Link
              to="/book"
              search={{ service: SERVICE_SLUG }}
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-gold-foreground"
            >
              <CalendarClock className="h-4 w-4" />
              Book consultation
            </Link>
            <a
              href="#quote"
              className="mt-3 inline-flex items-center gap-2 text-sm text-navy-foreground/80 hover:text-gold"
            >
              Or request a quote
            </a>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">
              Good to know
            </p>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>• Fixed fees on most CIPC filings.</li>
              <li>• Turnaround: 1–5 business days.</li>
              <li>• POPIA-aligned document handling.</li>
              <li>• Written engagement before work begins.</li>
            </ul>
          </div>
        </aside>
      </section>

      <FaqSection />

      <QuoteSection />

      <section className="bg-secondary/50 py-16">
        <div className="container-narrow">
          <h2 className="font-display text-2xl text-navy">Other services</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((s) => (
              <Link
                key={s.slug}
                to="/services/$slug"
                params={{ slug: s.slug }}
                className="group rounded-xl border border-border bg-card p-6 shadow-soft hover:-translate-y-0.5 hover:shadow-lifted transition"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-gold/15 text-navy">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-3 font-display text-lg text-navy">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function FaqSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="border-y border-border bg-card/50 py-16">
      <div className="container-narrow">
        <span className="gold-rule" />
        <h2 className="font-display text-3xl text-navy">
          Company Secretarial FAQs
        </h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Common questions about CIPC filings, MOIs, Beneficial Ownership and
          annual resolutions in South Africa.
        </p>
        <div className="mt-8 divide-y divide-border rounded-xl border border-border bg-card shadow-soft">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-display text-base text-navy">
                    {f.q}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-gold transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen ? (
                  <div className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const MAX_FILES = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_BYTES = 20 * 1024 * 1024;
const ALLOWED_EXT = ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt";
const ALLOWED_MIME = new Set<string>([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/plain",
]);
const ALLOWED_EXT_SET = new Set([
  "pdf","doc","docx","xls","xlsx","png","jpg","jpeg","webp","txt",
]);

function isAllowedFile(f: File): boolean {
  if (f.type && ALLOWED_MIME.has(f.type)) return true;
  const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
  return ALLOWED_EXT_SET.has(ext);
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      const i = r.indexOf(",");
      resolve(i >= 0 ? r.slice(i + 1) : r);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function QuoteSection() {
  const send = useServerFn(submitCosecQuote);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function humanSize(n: number) {
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
    return `${Math.max(1, Math.round(n / 1024))} KB`;
  }

  function addFiles(picked: FileList | null) {
    setFileErrors([]);
    if (!picked) return;
    const next = [...files];
    const errors: string[] = [];
    for (const f of Array.from(picked)) {
      if (next.length >= MAX_FILES) {
        errors.push(`"${f.name}" was skipped — you can attach at most ${MAX_FILES} files per request.`);
        continue;
      }
      if (!isAllowedFile(f)) {
        const ext = f.name.split(".").pop()?.toLowerCase() || "unknown";
        errors.push(
          `"${f.name}" — file type .${ext} is not allowed. Allowed: PDF, Word (.doc/.docx), Excel (.xls/.xlsx), images (.png/.jpg/.jpeg/.webp) or .txt.`,
        );
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        errors.push(
          `"${f.name}" is ${humanSize(f.size)} — that exceeds the 5 MB per-file limit. Please compress or split the document.`,
        );
        continue;
      }
      if (next.some((x) => x.name === f.name && x.size === f.size)) {
        errors.push(`"${f.name}" is already attached.`);
        continue;
      }
      next.push(f);
    }
    const total = next.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_BYTES) {
      errors.push(
        `Total attachments would be ${humanSize(total)} — the combined limit is 20 MB. Remove a file before adding more.`,
      );
      setFileErrors(errors);
      // surface a toast for immediate feedback but keep inline list as source of truth
      toast.error("Total attachments exceed 20 MB");
      return;
    }
    if (errors.length) {
      setFileErrors(errors);
      toast.error(errors.length === 1 ? errors[0] : `${errors.length} file(s) were rejected — see the list below.`);
    }
    setFiles(next);
  }


  function removeFile(i: number) {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
    setFileErrors([]);
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const full_name = String(fd.get("full_name") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const phone = String(fd.get("phone") || "").trim();
    const company = String(fd.get("company") || "").trim();
    const registration = String(fd.get("registration") || "").trim();
    const sub_service = String(fd.get("sub_service") || "").trim();
    const contact_method = String(fd.get("contact_method") || "Email").trim() as
      | "Email"
      | "Phone call"
      | "WhatsApp";
    const details = String(fd.get("details") || "").trim();

    if (full_name.length < 2 || !email || details.length < 5) {
      toast.error("Please complete name, email and a short description.");
      return;
    }

    setLoading(true);
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          contentType: f.type || "application/octet-stream",
          dataBase64: await fileToBase64(f),
        })),
      );
      const res = await send({
        data: {
          full_name,
          email,
          phone,
          company,
          registration,
          sub_service,
          contact_method,
          details,
          attachments,
        },
      });
      setDone(true);
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success(
        `Quote request received (Ref ${res.reference}). Confirmation sent via email${phone ? " and SMS" : ""}.`,
      );
      form.reset();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Could not send your request. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="quote" className="container-narrow py-16 scroll-mt-24">
      <div className="grid gap-10 md:grid-cols-[1fr_1.4fr]">
        <div>
          <span className="gold-rule" />
          <h2 className="font-display text-3xl text-navy">Request a quote</h2>
          <p className="mt-3 text-muted-foreground">
            Tell us about your company and what you need. We'll come back with a
            fixed-fee quote and turnaround within one business day.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-gold" /> Confirmation
              email &amp; SMS sent the moment you submit
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-gold" /> Attach MOI,
              CIPC forms and supporting documents (PDF / Word / images)
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-gold" /> POPIA-aligned
              handling of company data
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-gold" />
              <span>
                Not sure what to send?{" "}
                <a
                  href="/downloads/company-secretarial-documents-checklist.pdf"
                  download
                  className="underline hover:text-navy"
                >
                  Download our documents checklist
                </a>
                .
              </span>
            </li>
          </ul>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-xl border border-border bg-card p-6 shadow-soft"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name *" name="full_name" required maxLength={120} />
            <Field
              label="Email *"
              name="email"
              type="email"
              required
              maxLength={255}
            />
            <Field label="Phone (optional)" name="phone" maxLength={40} />
            <Field label="Company name" name="company" maxLength={120} />
            <Field
              label="CIPC registration no."
              name="registration"
              placeholder="e.g. 2023/123456/07"
              maxLength={40}
            />
            <div>
              <label className="text-sm font-medium text-navy">
                Service required
              </label>
              <select
                name="sub_service"
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                defaultValue=""
              >
                <option value="" disabled>
                  Select a service…
                </option>
                {SUB_SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-navy">
              Preferred contact method
            </label>
            <div className="mt-2 flex flex-wrap gap-4 text-sm">
              {["Email", "Phone call", "WhatsApp"].map((opt, i) => (
                <label key={opt} className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="contact_method"
                    value={opt}
                    defaultChecked={i === 0}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-navy">
              Details of what you need *
            </label>
            <textarea
              name="details"
              required
              minLength={5}
              maxLength={4000}
              rows={5}
              placeholder="e.g. We need to file Beneficial Ownership for our Pty (Ltd), update the registered address, and amend the MOI to add a new share class."
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-navy">
              Attach documents (optional)
            </label>
            <p className="text-xs text-muted-foreground">
              MOI, CIPC forms, resolutions, IDs. Up to {MAX_FILES} files, 5 MB
              each (20 MB total). PDF, Word, Excel, images.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-secondary/60">
                <Upload className="h-4 w-4" />
                Choose files
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_EXT}
                  className="hidden"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </label>
              <span className="text-xs text-muted-foreground">
                {files.length}/{MAX_FILES} selected
              </span>
            </div>
            {fileErrors.length > 0 && (
              <div
                role="alert"
                aria-live="polite"
                className="mt-2 rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-800"
              >
                <p className="font-semibold">We couldn't accept some files:</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {fileErrors.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
                <p className="mt-1 text-[11px] text-red-700">
                  Allowed types: PDF, Word, Excel, PNG/JPG/WEBP images, .txt. Limits: 5 MB per file, 20 MB total, {MAX_FILES} files max.
                </p>
              </div>
            )}
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-2 py-1 text-xs"
                  >
                    <span className="inline-flex items-center gap-2 truncate">
                      <Paperclip className="h-3 w-3 text-gold" />
                      <span className="truncate">{f.name}</span>
                      <span className="text-muted-foreground">
                        ({Math.round(f.size / 1024)} KB)
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-muted-foreground hover:text-red-600"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md bg-navy px-6 py-2.5 text-sm font-semibold text-navy-foreground shadow-soft hover:bg-navy/90 disabled:opacity-60"
          >
            {loading ? "Sending…" : "Request a quote"}
            {!loading ? <ArrowRight className="h-4 w-4" /> : null}
          </button>
          {done ? (
            <p className="text-sm text-emerald-700">
              Thanks — we've received your quote request. A confirmation email
              (and SMS if you provided a number) is on its way. We'll respond
              within one business day.
            </p>
          ) : null}
        </form>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  maxLength,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  maxLength?: number;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-navy">{label}</label>
      <input
        name={name}
        type={type}
        required={required}
        maxLength={maxLength}
        placeholder={placeholder}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
    </div>
  );
}
